import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  TextField,
  ListItem,
  useTheme,
  List,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import { AppState } from "../../../common/types";
import { DataProviderContext, useNotify, useTranslate } from "react-admin";
import { ApiManager } from "../../../common/data/ApiManager";
import AddCircleRoundedIcon from "@material-ui/icons/AddCircleRounded";
import { useDebounce } from "use-debounce";
import { RecordStatus } from "../../../common/data/service-proxies";
import Axios, { CancelTokenSource } from "axios";
import { ISelectedDriverOrVehicle, GroupMembersContext } from "./context";
import { OPERATION_CANCELED } from "../../../common/data/axiosErrorHandler";

interface DriverSelectorProps {
  addMember: (driver: ISelectedDriverOrVehicle) => void;
  dtoNameField: "driverName" | "name";
  dtoPrefixField?: keyof DriverOrVehicleDto;
  resourceName: string;
  contextField: "selectedDrivers" | "selectedVehicles";
  title: string;
  disabled?: boolean;
}

type DriverOrVehicleDto = {
  id?: number;
  driverName?: string;
  driverId?: string;
  name?: string;
  status?: RecordStatus;
};

export const MemberSelector = <T extends DriverOrVehicleDto>({
  addMember,
  dtoNameField,
  dtoPrefixField,
  resourceName,
  contextField,
  title,
  disabled,
}: DriverSelectorProps) => {
  const theme = useTheme();
  const themeName = useSelector((x: AppState) => x.theme);
  const [searchText, setSearchText] = useState("");
  const [unassignedMembers, setUnassignedMembers] = useState<T[]>([]);
  const dataProvider = useContext(DataProviderContext) as ApiManager;
  dataProvider.setSearchFields(resourceName, [dtoNameField]);
  const [debouncedInput] = useDebounce(searchText, 500);
  const context = useContext(GroupMembersContext);
  const notify = useNotify();
  const cancellationToken = useRef<CancelTokenSource>();
  const translate = useTranslate();

  useEffect(() => {
    const filter: {
      q?: string;
      _not: Array<{ field: string; value: any }>;
    } =
      debouncedInput.length > 0
        ? { q: debouncedInput, _not: [] }
        : { _not: [] };

    for (const addedMember of context[contextField].filter((x) =>
      Boolean(x.id)
    )) {
      if (addedMember.id) {
        filter._not = [...filter._not, { field: "id", value: addedMember.id }];
      }
    }

    if (cancellationToken.current) {
      cancellationToken.current.cancel();
    }

    cancellationToken.current = Axios.CancelToken.source();
    dataProvider
      .getList<T & { id: string }>(
        resourceName,
        {
          pagination: { page: 1, perPage: 400 },
          sort: { field: dtoNameField, order: "asc" },
          filter,
        },
        undefined,
        { cancelToken: cancellationToken.current?.token }
      )
      .then((result) => {
        setUnassignedMembers(result.data);
      })
      .catch((err) => {
        if (err.message !== OPERATION_CANCELED) {
          notify(err.message, "error");
        }
      });
    // Disabling react-hooks/exhaustive-deps because we cover context through both props separately
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataProvider,
    debouncedInput,
    notify,
    context.selectedDrivers,
    context.selectedVehicles,
    contextField,
    resourceName,
  ]);

  return (
    <Box
      className="simple-list-placeholder"
      style={{
        borderColor:
          themeName === "dark"
            ? theme.palette.grey[600]
            : theme.palette.grey[400],
      }}>
      <Typography className="simple-list-title" variant="body2">
        {title}
      </Typography>
      <TextField
        className="simple-list-search-box"
        size="small"
        variant="outlined"
        label={translate("resources.common.labels.search")}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
      />
      <List
        className="simple-list"
        style={{
          borderColor:
            themeName === "dark"
              ? theme.palette.grey[600]
              : theme.palette.grey[400],
        }}>
        {unassignedMembers.map((member) => (
          <ListItem key={member.id}>
            {
              <div className="simple-list-item">
                <div className="record-info">
                  <Typography variant="body2">
                    {dtoPrefixField && member[dtoPrefixField]
                      ? `${member[dtoPrefixField]} `
                      : null}
                    {member[dtoNameField]}
                  </Typography>
                  <Typography variant="caption">{member.status}</Typography>
                </div>
                {!disabled ? (
                  <IconButton
                    style={{ color: theme.palette.success.main }}
                    onClick={() =>
                      addMember({
                        id: member.id!,
                        name: member[dtoNameField] ?? "???",
                        status: member.status!,
                      })
                    }>
                    <AddCircleRoundedIcon />
                  </IconButton>
                ) : null}
              </div>
            }
          </ListItem>
        ))}
      </List>
    </Box>
  );
};
